// 261005 E4-P4b — hàm THUẦN của "Xử lý phần mới": 6 bước, dấu chip, chữ xác nhận, mã lỗi, quyết định poll. KHÔNG import antd / react (jest của react-scripts không load được antd v6).
import type { AppendRun, WorkbenchRunningJob, WorkbenchStageKey, WorkbenchView } from '../../../types/adaptationWorkbench';
import type { ScanEstimate } from '../../../types/adaptation';
import { formatJobStepProgress } from './jobStepProgress';
import type { StageMarkView, WorkbenchTarget } from './workbenchView';
import { friendlyApiMessage, friendlyJobNote } from './jobErrorText';
import { isOverviewWriterCode } from './overview/overviewErrors';

type ViewLike = Pick<WorkbenchView, 'appendRun' | 'interrupted' | 'newChapters' | 'warnResplit' | 'newChapterRange'>;

// ─── Poll (B2, đã chỉnh — xem dev-report) ───────────────────────────────────

/**
 * 261015 K-b — chuỗi còn có thể TỰ chạy tiếp (khoá ghi ② ④ ③ + giữ poll): `RUNNING` mà chưa dừng vì lỗi. Giống `isAppendRunHoldingEdits` của BE
 * (khoảng hở mili-giây giữa hai bước không có `error`; chuỗi đã dừng vì lỗi — vd `STEP_GATE_BLOCKED`, `EPISODES_BEYOND_CHAPTER_UNITS` — luôn có `error`).
 * `RUNNING + error` = đang chờ người: KHÔNG coi cả bàn là bận, để người sửa nguyên nhân (đổi số tập ở ②, sửa ④, vượt cổng ③) rồi bấm "Chạy tiếp".
 */
export const appendRunHoldsEdits = (run: Pick<AppendRun, 'status' | 'error'> | null | undefined): boolean => run?.status === 'RUNNING' && !run.error;

/**
 * Còn việc đang diễn ra: có job con HOẶC chuỗi đang tự chạy (`appendRunHoldsEdits`). Cố ý tính cả `interrupted` KHÔNG kèm lỗi: khoảng hở mili-giây
 * giữa job xong và lần ghi `WAIT_SKELETON` trông y như gián đoạn thật; nếu dừng poll ở đó thì ② không bao giờ nạp lại.
 * 261023 E3-P3c1: chuỗi truyện mới TRƯỚC khi lưu khung xương đang `RUNNING` / `BLOCKED_BY_JOB` cũng giữ poll dù 0 job.
 * 261023 E3-P3c2a (SA NOTE 4): `INTERRUPTED` và `WAIT_BUDGET` ở chỗ này là chuỗi đang CHỜ NGƯỜI (bấm «Chạy tiếp» / «Nâng trần & chạy tiếp») ⇒ KHÔNG bận;
 * nếu vẫn poll thì GET workbench chạy vô hạn. Giá phải trả: khoảng hở mili-giây giữa job quét xong và job khung xương bắt đầu có thể đọc ra `INTERRUPTED` — poll dừng
 * ở đó, `shouldFireFinished` nạp lại một lần, và lần nạp sau (hoặc bấm «Chạy tiếp») mới thấy job khung xương. SAU khi lưu khung xương, `appendRun.status = 'WAIT_BUDGET'`
 * không phải `RUNNING` nên `appendRunHoldsEdits` đã false sẵn.
 */
const newStoryRunHolds = (n: Pick<WorkbenchView, 'newStoryRun'>['newStoryRun']): boolean => !!n && n.state !== 'INTERRUPTED' && n.state !== 'WAIT_BUDGET';
export const isWorkbenchBusy = (w: Pick<WorkbenchView, 'jobs' | 'appendRun' | 'newStoryRun'> | null | undefined): boolean =>
  !!w && ((w.jobs?.running?.length ?? 0) > 0 || appendRunHoldsEdits(w.appendRun) || newStoryRunHolds(w.newStoryRun));
export const shouldPollWorkbench = isWorkbenchBusy;
/** Báo "xong" MỘT lần khi từ bận → không bận. */
export const shouldFireFinished = (prevBusy: boolean | null, w: Pick<WorkbenchView, 'jobs' | 'appendRun' | 'newStoryRun'>): boolean => prevBusy === true && !isWorkbenchBusy(w);

// ─── 6 bước ─────────────────────────────────────────────────────────────────

export type ChainStepState = 'done' | 'run' | 'wait' | 'todo' | 'error' | 'skipped';
export interface ChainStep { key: string; label: string; stage: WorkbenchStageKey; state: ChainStepState; human: boolean; detail?: string }

const STEP_DEFS: Array<{ key: string; label: string; stage: WorkbenchStageKey; human: boolean }> = [
  { key: 'SCAN', label: 'Quét phần mới', stage: 'source', human: false },
  { key: 'SKELETON', label: 'Gộp vào khung xương', stage: 'skeleton', human: false },
  { key: 'REVIEW', label: 'Duyệt khung xương (bạn)', stage: 'skeleton', human: true },
  { key: 'ALIAS_LOCALIZE', label: 'Đổi tên nhân vật mới', stage: 'localize', human: false },
  { key: 'SPLIT', label: 'Chia tập mới', stage: 'split', human: false },
  { key: 'WRITE', label: 'Viết tập mới (bạn)', stage: 'script', human: true },
];
const STEP_INDEX: Record<string, number> = { SCAN: 0, SKELETON: 1, ALIAS_LOCALIZE: 3, SPLIT: 4, WRITE: 5 };

/** D5 (Leader): có `note` VÀ đã qua STOP 1 (`approvedAt`) ⇒ bước đổi tên = đã bỏ qua. */
export const localizeSkipped = (run: Pick<AppendRun, 'note' | 'approvedAt'> | null | undefined): boolean => !!run?.note && !!run.approvedAt;

/** Vị trí "đang đứng" trong 6 bước; -1 = chưa có chuỗi. 6 = đã xong cả 5 bước máy (đang chờ viết = 5). */
const positionOf = (run: AppendRun | null | undefined): number => {
  if (!run || run.status === 'CANCELLED') return -1;
  if (run.status === 'WAIT_SKELETON') return 2;
  if (run.status === 'WAIT_EPISODE_PLAN') return 5;
  if (run.status === 'DONE') return 5;
  return run.step ? (STEP_INDEX[run.step] ?? 0) : 0;
};

export const chainSteps = (view: Pick<ViewLike, 'appendRun' | 'interrupted'>): ChainStep[] => {
  const run = view.appendRun ?? null;
  const pos = positionOf(run);
  return STEP_DEFS.map((d, i): ChainStep => {
    let state: ChainStepState = 'todo';
    if (pos >= 0) {
      if (i < pos) state = 'done';
      else if (i === pos) {
        if (run!.status === 'RUNNING') state = run!.error ? 'error' : view.interrupted ? 'wait' : 'run';
        else state = 'wait'; // WAIT_SKELETON (bước duyệt) hoặc DONE (bước viết)
      }
    }
    let detail: string | undefined;
    if (d.key === 'ALIAS_LOCALIZE' && (state === 'done') && localizeSkipped(run)) { state = 'skipped'; detail = run!.note; }
    if (state === 'error') detail = friendlyJobNote(run!.error?.message) || undefined;
    return { key: d.key, label: d.label, stage: d.stage, state, human: d.human, ...(detail ? { detail } : {}) };
  });
};

export const STEP_ICON: Record<ChainStepState, string> = { done: '✓', run: '⏳', wait: '⏸', todo: '○', error: '⚠', skipped: '↷' };

// ─── Dải: loại hiển thị ─────────────────────────────────────────────────────

export type BarKind = 'none' | 'start' | 'running' | 'interrupted' | 'wait' | 'done' | 'cancelled';
export const barKind = (view: ViewLike | null | undefined, dismissedId: string | null): BarKind => {
  if (!view) return 'none';
  const run = view.appendRun ?? null;
  if (run?.status === 'RUNNING') return view.interrupted ? 'interrupted' : 'running';
  if (run?.status === 'WAIT_SKELETON' || run?.status === 'WAIT_EPISODE_PLAN') return 'wait';
  if ((view.newChapters ?? 0) > 0) return 'start';
  if (run && run.id !== dismissedId) return run.status === 'DONE' ? 'done' : run.status === 'CANCELLED' ? 'cancelled' : 'none';
  return 'none';
};

export const rangeText = (r: { from: number; to: number } | null | undefined): string =>
  !r ? '' : r.from === r.to ? `chương ${r.from}` : `chương ${r.from}–${r.to}`;

/** Khoảng chương của dải: ưu tiên `newChapterRange`; không có (sau khi xong) ⇒ theo `appendRun`. */
export const barRange = (view: ViewLike): { from: number; to: number } | null =>
  view.newChapterRange ?? (view.appendRun ? { from: view.appendRun.fromOrderIndex, to: view.appendRun.lastOrderIndex } : null);

export const barTitle = (view: ViewLike): string => {
  const rt = rangeText(barRange(view));
  const run = view.appendRun;
  const m = run && run.status !== 'CANCELLED' ? run.lockedThrough : null;
  return `Phần mới${rt ? `: ${rt}` : ''}${m && m > 0 ? ` · tập 1–${m} đã viết được giữ nguyên` : ''}`;
};

// ─── Dấu chip chặng (D4) ────────────────────────────────────────────────────

export interface MarkCtx { newEpisodes: number; dismissedId: string | null }
const mk = (tone: StageMarkView['tone'], icon: string, text: string, tip: string): StageMarkView => ({ tone, icon, text, tip, attention: false });
const STAGE_ORDER_LOCAL: WorkbenchStageKey[] = ['source', 'skeleton', 'localize', 'split', 'script'];

/** Ghi đè dấu chip theo chuỗi. Chưa bấm Xử lý: chỉ ① "mới". Rỗng ⇒ không ghi đè gì. */
export const stageMarks = (view: ViewLike | null | undefined, ctx: MarkCtx): Partial<Record<WorkbenchStageKey, StageMarkView>> => {
  if (!view) return {};
  const run = view.appendRun ?? null;
  const out: Partial<Record<WorkbenchStageKey, StageMarkView>> = {};
  const active = run && (run.status === 'RUNNING' || run.status === 'WAIT_SKELETON' || run.status === 'WAIT_EPISODE_PLAN');
  if (!active) {
    if (run?.status === 'DONE' && run.id !== ctx.dismissedId) {
      for (const k of STAGE_ORDER_LOCAL) out[k] = mk('ok', '✓', '', 'Phần mới xong');
      out.script = ctx.newEpisodes > 0
        ? mk('warn', '●', `${ctx.newEpisodes} mới`, `${ctx.newEpisodes} tập mới chờ viết`)
        : mk('ok', '✓', '', 'Phần mới xong');
      return out;
    }
    if ((view.newChapters ?? 0) > 0) out.source = mk('warn', '●', 'mới', `${view.newChapters} chương mới chưa xử lý`);
    return out;
  }
  const cur = run!.status === 'WAIT_SKELETON' ? 'skeleton' : STEP_DEFS[positionOf(run)].stage;
  const ci = STAGE_ORDER_LOCAL.indexOf(cur);
  STAGE_ORDER_LOCAL.forEach((k, i) => {
    if (i < ci) out[k] = mk('ok', '✓', '', 'Phần mới xong');
    else if (i > ci) out[k] = mk('lock', '⏸', '', 'Chờ bước trước của phần mới');
    else if (run!.status === 'WAIT_SKELETON') out[k] = mk('warn', '●', 'duyệt', 'Chờ bạn duyệt khung xương');
    else if (run!.status === 'WAIT_EPISODE_PLAN') out[k] = mk('warn', '●', 'chốt', 'Chờ bạn chốt dàn tập');
    else if (run!.error || view.interrupted) out[k] = mk('warn', '●', 'dừng', friendlyJobNote(run!.error?.message) || 'Đã dừng — bấm "Chạy tiếp"');
    else out[k] = mk('run', '⏳', '', 'Đang xử lý phần mới');
  });
  return out;
};

// ─── Chữ xác nhận / lỗi ─────────────────────────────────────────────────────

export interface StartInfo { newChapters: number; range?: { from: number; to: number } | null; episodesListed: number; warnResplit: number }

/** M = số tập đã khoá = tổng tập − số tập sẽ chia lại. */
export const lockedFromWorkbench = (episodesListed: number, warnResplit: number): number => Math.max(0, episodesListed - Math.max(0, warnResplit));

export const startConfirmText = (i: StartInfo): string[] => {
  const m = lockedFromWorkbench(i.episodesListed, i.warnResplit);
  const rt = rangeText(i.range);
  const lines = [`${i.newChapters} chương mới${rt ? ` (${rt})` : ''}.`];
  lines.push(m > 0 ? `Tập 1–${m} (đã viết / đã đẩy) giữ nguyên.` : 'Chưa tập nào bị khoá.');
  if (i.warnResplit > 0) lines.push(`${i.warnResplit} tập chưa viết (${m + 1}–${i.episodesListed}) sẽ được chia lại cùng phần mới.`);
  return lines;
};

// 261005 E5: bỏ dòng giá trong hộp xác nhận.
export const costLines = (_est: ScanEstimate | null | undefined): string[] => [];

export interface ConfirmCopy { title: string; content: string; okText: string; danger: boolean }

/** Hoàn tác gộp chỉ khi chưa `continue` và BE đã lưu `preMergeVersionId` (cùng điều kiện `planCancel`). */
export const cancelWillRestore = (run: Pick<AppendRun, 'approvedAt' | 'preMergeVersionId'>): boolean => !run.approvedAt && !!run.preMergeVersionId;
export const cancelCopy = (run: Pick<AppendRun, 'approvedAt' | 'preMergeVersionId'>): ConfirmCopy =>
  cancelWillRestore(run)
    ? { title: 'Huỷ phần mới?', content: 'Huỷ sẽ hoàn tác lần gộp, kể cả sửa tay ở ② từ lúc gộp.', okText: 'Huỷ & hoàn tác', danger: true }
    : { title: 'Dừng xử lý phần mới?', content: 'Dừng chuỗi — dữ liệu đã ghi giữ nguyên.', okText: 'Dừng chuỗi', danger: true };

const ERROR_TEXT: Record<string, string> = {
  OUTLINE_MISSING: 'Chưa có khung xương.',
  OUTLINE_NOT_APPROVED: 'Khung xương chưa được duyệt — duyệt rồi mới xử lý phần mới.',
  EPISODES_MISSING: 'Chưa chia tập (hoặc tập đã viết thiếu chương nguồn) — chưa nối đuôi được.',
  NEW_CHAPTER_INSIDE_LOCKED: 'Có chương mới chen vào phần đã khoá — xoá hoặc đặt lại số thứ tự chương.',
  NOTHING_NEW: 'Không có chương mới nào chưa thuộc tập.',
  JOB_RUNNING: 'Truyện đang có tiến trình chạy — chờ xong rồi thử lại.',
  APPEND_RUN_ACTIVE: 'Đang có một đợt xử lý phần mới — chờ tới điểm dừng hoặc huỷ đợt.',
  NO_APPEND_RUN: 'Chưa có đợt xử lý phần mới.',
  NOT_WAITING: 'Đợt này không đang chờ duyệt khung xương.',
  NOT_INTERRUPTED: 'Đợt này vẫn đang chạy — chưa cần chạy tiếp.',
  NOT_WAITING_GATE: 'Đợt này không còn chờ quyết lỗi cổng AI (đã đi tiếp hoặc đã dừng) — đã tải lại.',
  CHAPTERS_CHANGED: 'Danh sách chương đã đổi từ lúc bắt đầu — huỷ rồi bắt đầu lại.',
  NO_EPISODE_FOR_TAIL: 'Không còn tập nào cho phần mới — tăng số tập ở ② rồi chạy tiếp.',
  HAS_ERRORS: 'Khung xương còn lỗi — sửa hết lỗi ở ② rồi bấm lại.',
  NEEDS_APPROVAL: 'Khung xương đã bị sửa về bản nháp trong lúc dừng — bấm "Duyệt khung xương" (hoặc "Chốt tổng quan") ở ② rồi "Chạy tiếp".',   // 261015: không tự duyệt thay người
  OVERVIEW_NOT_EFFECTIVE: 'Tổng quan truyện chưa chốt hoặc đã cũ — mở "Tổng quan truyện" ở ② để chốt lại, rồi bấm lại nút vừa rồi. Chưa có gì được ghi, chưa tốn AI.',   // 261015 P4c-2b (D9b): STOP 2 (thanh nối đuôi) gọi thẳng chốt dàn tập; chữ trung lập tên nút vì nút ở thanh và ở thẻ Chốt ④ khác tên
  OVERVIEW_CHANGED: 'Tổng quan truyện vừa đổi trong lúc viết — mở "Tổng quan truyện" ở ② kiểm tra (chốt lại nếu cần) rồi bấm "Chạy tiếp". Chưa có gì được ghi cho tập bị chặn.',   // 261015 P4c-2c-1 (Q3): lô viết dừng vì tổng quan ⇒ đợt GIỮ RUNNING + ghi lỗi (không DONE)
  PRE_MERGE_VERSION_GONE: 'Không hoàn tác được lần gộp (phiên bản cũ đã bị dọn) — đợt đã được huỷ, khung xương giữ như hiện tại.',
  GENRE_MISSING: 'Thiếu thể loại ở khung xương.',
  SCAN_FAILED: 'Quét phần mới lỗi.',
};
/** 261015 P4c-2b m1 — nơi `handleAppendError` đưa người tới theo mã lỗi (null = ở yên): `HAS_ERRORS` ⇒ ②; `OVERVIEW_NOT_EFFECTIVE` ⇒ ② mở tab Tổng quan truyện (cùng đích với `nextActionTarget` OVERVIEW_MISSING). */
export const appendErrorGoTo = (code: string | undefined): WorkbenchTarget | null => (
  code === 'HAS_ERRORS' ? { stage: 'skeleton' } : (code === 'OVERVIEW_NOT_EFFECTIVE' || code === 'OVERVIEW_CHANGED') ? { stage: 'skeleton', intent: 'openOverview' } : null   // 261015 P4c-2c-1: + OVERVIEW_CHANGED
);
/**
 * 261015 P4c-2c-1 (F2 review FE) — thanh "Xử lý phần mới" đang GIÁN ĐOẠN (`RUNNING + error`) vì lô viết dừng do tổng quan (`run.error.code` = `OVERVIEW_NOT_EFFECTIVE` | `OVERVIEW_CHANGED`, BE `appendRunService`):
 * chữ dòng dưới thanh = câu của FE (không phải message thô của BE) + lối ra "Mở Tổng quan truyện" cạnh "Chạy tiếp". Mã khác / không có lỗi ⇒ chữ cũ, KHÔNG nút.
 * Chữ dùng cho `run.error` ĐÃ LƯU nên nói "Chạy tiếp" (khác `ERROR_TEXT.OVERVIEW_NOT_EFFECTIVE` — câu cho lỗi API "bấm lại nút vừa rồi").
 */
const INTERRUPTED_OVERVIEW_TEXT: Record<'OVERVIEW_NOT_EFFECTIVE' | 'OVERVIEW_CHANGED', string> = {
  OVERVIEW_NOT_EFFECTIVE: 'Tổng quan truyện chưa chốt hoặc đã cũ nên lô viết tập dừng — mở "Tổng quan truyện" ở ② để chốt lại rồi bấm "Chạy tiếp". Chưa có gì được ghi cho tập bị chặn.',
  OVERVIEW_CHANGED: 'Tổng quan truyện vừa đổi trong lúc viết nên lô viết tập dừng — mở "Tổng quan truyện" ở ② kiểm tra (chốt lại nếu cần) rồi bấm "Chạy tiếp". Chưa có gì được ghi cho tập bị chặn.',
};
/** 261023 3g (Q5): mã BE khi số tập vượt số chương có thể chia — dải KHÔNG nói «gián đoạn» mà chỉ đường sửa số tập ở ② (không nhắc `range`). */
export const EPISODES_BEYOND_CHAPTER_UNITS_CODE = 'EPISODES_BEYOND_CHAPTER_UNITS';
const EPISODES_BEYOND_TEXT_NO_MAX = 'Số tập nhiều hơn số chương có thể chia. Giảm số tập ở Khung xương rồi Duyệt & chạy tiếp.';
export const interruptedStripView = (run: Pick<AppendRun, 'error'> | null | undefined): { text: string; goTo: WorkbenchTarget | null } => {
  const code = run?.error?.code;
  if (code === EPISODES_BEYOND_CHAPTER_UNITS_CODE) {
    // Chữ BE mới có «(tối đa N)»; lỗi lưu từ trước 3g mang chữ cũ (nhắc `range`) hoặc không có chữ ⇒ câu chung, không lộ chữ cũ.
    const msg = String(run?.error?.message ?? '');
    return { text: /^Số tập nhiều hơn số chương có thể chia \(tối đa \d+\)\./.test(msg) ? msg : EPISODES_BEYOND_TEXT_NO_MAX, goTo: { stage: 'skeleton' } };
  }
  if (isOverviewWriterCode(code)) return { text: `Bị gián đoạn: ${INTERRUPTED_OVERVIEW_TEXT[code]}`, goTo: appendErrorGoTo(code) };
  const note = friendlyJobNote(run?.error?.message);
  return { text: note ? `Bị gián đoạn: ${note}` : 'Bị gián đoạn (không còn tiến trình chạy) — bấm "Chạy tiếp"', goTo: null };
};

/** Chữ cho mã lỗi `append-run*`; mã lạ ⇒ dùng `fallback` (message của BE). */
export const errorText = (code: string | undefined, fallback?: string): string => (code && ERROR_TEXT[code]) || friendlyApiMessage(fallback, '') || 'Không xử lý được phần mới';

// ─── ② STOP 1 ──────────────────────────────────────────────────────────────

export const stopOneAlertText = (run: Pick<AppendRun, 'newEntities' | 'episodesBefore' | 'episodesAfter'>): string => {
  const n = run.newEntities;
  const parts = [`+${n?.characterIds.length ?? 0} nhân vật`, `+${n?.locationIds.length ?? 0} bối cảnh`, `+${n?.addedBeats ?? 0} cao trào`];
  const eps = run.episodesBefore && run.episodesAfter ? ` · số tập ${run.episodesBefore} → ${run.episodesAfter} (AI đề xuất)` : '';
  return `Phần mới: ${parts.join(' · ')}${eps}`;
};

/**
 * L2: BE từ chối số tập < số tập đang có; và tổng ≤ M ⇒ `NO_EPISODE_FOR_TAIL`.
 * 261015 K-c: danh sách tập đã LỖI THỜI (`episodesStale`) ⇒ độ dài của nó không phải "số tập đang có" — BE chỉ còn chặn dưới M (tập đã khoá) ⇒ mốc dưới = M + 1 (tổng phải > M).
 */
export const episodeCountBounds = (a: { episodesLen: number; lockedThrough: number; stale?: boolean; chapterUnitMax?: number | null }): { min: number; max: number | null; help: string } => {
  // 3g (Q5): trần số tập = số chương nguồn thô (chuỗi Truyện mới chia theo chương). Chỉ GỢI Ý + báo đỏ khi vượt (`episodeCountOver`) — KHÔNG tự kẹp giá trị người gõ.
  const max = typeof a.chapterUnitMax === 'number' && Number.isFinite(a.chapterUnitMax) ? a.chapterUnitMax : null;
  const maxHelp = max === null ? '' : ` Tối đa ${max} tập (mỗi tập ít nhất một chương).`;
  if (a.stale) return { min: a.lockedThrough + 1, max, help: `Tối thiểu ${a.lockedThrough + 1}: phải lớn hơn ${a.lockedThrough} tập đã khoá. Danh sách tập đang lỗi thời nên không bị giới hạn bởi số tập cũ — sẽ chia lại.${maxHelp}` };
  const min = Math.max(a.episodesLen, a.lockedThrough + 1);
  return { min, max, help: `Tối thiểu ${min}: không giảm xuống dưới số tập đang có (${a.episodesLen}) và phải lớn hơn ${a.lockedThrough} tập đã khoá.${maxHelp}` };
};

/** 3g (Q5): giá trị đang gõ vượt trần ⇒ ô đỏ + khoá nút «Sửa số tập». Không có trần / chưa gõ ⇒ false. */
export const episodeCountOver = (value: number | null | undefined, max: number | null | undefined): boolean =>
  typeof value === 'number' && typeof max === 'number' && value > max;

export const REVIEW_CORE_PROMPT = 'Phần mới vừa được gộp vào khung xương. Cốt lõi và các phần "giữ / cắt" vẫn dựa trên phần cũ — hãy xem lại cốt lõi và đề xuất chỉnh nếu phần mới làm nó lệch.';

/** Nhãn "Mới" ở bảng nhân vật / bối cảnh: chỉ ở STOP 1 (D6). */
export const newEntityIds = (run: AppendRun | null | undefined, kind: 'character' | 'location'): Set<string> =>
  new Set(run?.status === 'WAIT_SKELETON' ? (kind === 'character' ? run.newEntities?.characterIds : run.newEntities?.locationIds) ?? [] : []);

/** "Duyệt & chạy tiếp" (N2): tắt khi còn ERROR. */
export const continueDisabledReason = (a: { canApprove: boolean; reason?: string }): string | null => (a.canApprove ? null : (a.reason || 'Còn lỗi ở khung xương — sửa hết lỗi trước'));

// ─── STOP 2 ─────────────────────────────────────────────────────────────────

export const newEpisodeCount = (run: Pick<AppendRun, 'status' | 'lockedThrough'> | null | undefined, episodesListed: number): number =>
  run?.status === 'DONE' ? Math.max(0, episodesListed - run.lockedThrough) : 0;
export const stopTwoAlertText = (k: number): string => `Đã chia ${k} tập mới — chọn tập để viết kịch bản (không tự viết).`;

// ─── Nhớ đã đóng / đã áp lọc (D7) ──────────────────────────────────────────

export const DISMISS_KEY = (storyId: string): string => `talebox.adaptation.appendBar.dismissed.${storyId}`;
export const FILTER_SEEN_KEY = (storyId: string): string => `talebox.adaptation.appendFilter.seen.${storyId}`;
type StoreLike = Pick<Storage, 'getItem' | 'setItem'>;
const defaultStore = (): StoreLike | null => { try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; } };
export const readId = (key: string, store: StoreLike | null = defaultStore()): string | null => { try { return store?.getItem(key) ?? null; } catch { return null; } };
export const writeId = (key: string, id: string, store: StoreLike | null = defaultStore()): void => { try { store?.setItem(key, id); } catch { /* không nhớ được cũng không sao */ } };

/** Lọc mặc định "Tập mới" ở ⑤: DONE + chưa áp cho `id` này. */
export const shouldDefaultNewFilter = (run: Pick<AppendRun, 'id' | 'status'> | null | undefined, seenId: string | null): boolean => run?.status === 'DONE' && run.id !== seenId;

// ─── Tiến độ bước ───────────────────────────────────

const STEP_JOB_KIND: Record<string, WorkbenchRunningJob['kind']> = { SCAN: 'scan', SKELETON: 'skeleton', ALIAS_LOCALIZE: 'localize', SPLIT: 'split', WRITE: 'write' };
/** Tiến độ bước từ job con đang chạy (E4-P2). Không job / không tiến độ ⇒ ''. */
export const stepProgressText = (run: Pick<AppendRun, 'status' | 'step'> | null | undefined, running: ReadonlyArray<WorkbenchRunningJob> | undefined): string => {
  if (!run || run.status !== 'RUNNING' || !run.step) return '';
  const j = (running ?? []).find((x) => x.kind === STEP_JOB_KIND[run.step as string]);
  return formatJobStepProgress(j?.progress);
};
